import React from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@iconify/react';
import { Widget } from '@heroui-pro/react';
import type { RevenueDynamics } from '@/api/models';
import { formatCurrencyValue } from '@/utils/currency';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import { RevenueDynamicsProductPicker } from './RevenueDynamicsProductPicker';
import { RevenueDynamicsTooltip } from './RevenueDynamicsTooltip';
import {
    MOCK_REVENUE_DYNAMICS_PRODUCTS,
    formatBucketLabel,
    formatCompactTick,
} from './revenueDynamicsMeta';
import { RevenueDynamicsKpis } from './RevenueDynamicsKpis';
import { ComposedChart } from '@heroui-pro/react/composed-chart';

type RevenueDynamicsViewProps = {
    data: RevenueDynamics;
    productId: string | null;
    setProductId: (id: string | null) => void;
};

const CHART_HEIGHT_PX = 280;
const REVENUE_COLOR = 'var(--chart-3)';
const MARKUP_COLOR = 'var(--chart-4)';

export const RevenueDynamicsView = ({
    data,
    productId,
    setProductId,
}: RevenueDynamicsViewProps) => {
    const { t, i18n } = useTranslation('pages');

    const copy = React.useMemo(
        () => ({
            title: t('sales.widgets.revenueDynamics.title'),
            subtitle: t('sales.widgets.revenueDynamics.subtitle'),
            chartAria: t('sales.widgets.revenueDynamics.chartAria'),
            legendCaption: t('sales.widgets.revenueDynamics.legendCaption'),
            revenueLabel: t('sales.widgets.revenueDynamics.series.revenue'),
            markupLabel: t('sales.widgets.revenueDynamics.series.markup'),
        }),
        [t],
    );

    const formatRevenue = React.useCallback(
        (value: number | string) => formatCurrencyValue(data.currency, value),
        [data.currency],
    );

    // Compact thousand/million suffixes so the axis stays narrow. The
    // arithmetic lives in the meta file — a View formats nothing of its own.
    const formatYAxisTick = React.useCallback(
        (value: number) => formatCompactTick(value),
        [],
    );

    // Keyed on the language too: the axis renders the returned strings
    // verbatim, so switching to Georgian has to rebuild the labels.
    const formatXAxisTick = React.useCallback(
        (value: string) =>
            formatBucketLabel(value, data.granularity, i18n.language),
        [data.granularity, i18n.language],
    );

    return (
        <section
            aria-labelledby="revenue-dynamics-widget-title"
            {...getNodeTestIdProp('revenue-dynamics-section')}
        >
            <Widget
                className="flex flex-col gap-4"
                {...getNodeTestIdProp('revenue-dynamics-widget')}
            >
                <Widget.Header
                    className="pt-4"
                    {...getNodeTestIdProp('revenue-dynamics-widget-header')}
                >
                    <Widget.Title
                        className="flex items-center gap-2"
                        {...getNodeTestIdProp('revenue-dynamics-widget-title')}
                    >
                        <div
                            aria-hidden
                            className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-accent/10 text-accent"
                            {...getNodeTestIdProp('revenue-dynamics-icon-wrap')}
                        >
                            <Icon
                                className="size-4"
                                icon="solar:graph-up-bold-duotone"
                                {...getNodeTestIdProp('revenue-dynamics-icon')}
                            />
                        </div>
                        {copy.title}
                    </Widget.Title>
                    <Widget.Description
                        {...getNodeTestIdProp(
                            'revenue-dynamics-widget-subtitle',
                        )}
                    >
                        {copy.subtitle}
                    </Widget.Description>
                </Widget.Header>
                <Widget.Content className="flex flex-col gap-4">
                    <div
                        className="flex flex-wrap items-center gap-3"
                        {...getNodeTestIdProp('revenue-dynamics-toolbar')}
                    >
                        <RevenueDynamicsProductPicker
                            productId={productId}
                            products={MOCK_REVENUE_DYNAMICS_PRODUCTS}
                            setProductId={setProductId}
                        />
                    </div>
                    <div
                        aria-label={copy.chartAria}
                        role="img"
                        style={{ height: CHART_HEIGHT_PX }}
                        {...getNodeTestIdProp('revenue-dynamics-chart')}
                    >
                        <ComposedChart
                            data={
                                data.buckets as unknown as Record<
                                    string,
                                    number | string
                                >[]
                            }
                            height={CHART_HEIGHT_PX}
                        >
                            <ComposedChart.Grid vertical={false} />
                            <ComposedChart.XAxis
                                dataKey="date"
                                tickFormatter={formatXAxisTick}
                                tickMargin={8}
                            />
                            <ComposedChart.YAxis
                                tickFormatter={formatYAxisTick}
                                width={48}
                            />
                            <ComposedChart.Bar
                                barSize={18}
                                dataKey="revenue"
                                fill={REVENUE_COLOR}
                                name={copy.revenueLabel}
                                radius={[4, 4, 0, 0]}
                            />
                            <ComposedChart.Line
                                dataKey="markup"
                                dot={false}
                                name={copy.markupLabel}
                                stroke={MARKUP_COLOR}
                                strokeWidth={2}
                                type="monotone"
                            />
                            <ComposedChart.Tooltip
                                content={
                                    <RevenueDynamicsTooltip
                                        labelFormatter={formatXAxisTick}
                                        valueFormatter={formatRevenue}
                                    />
                                }
                                cursor={{ fill: 'var(--surface-secondary)' }}
                            />
                        </ComposedChart>
                    </div>
                    <div
                        className="text-right text-[11px] text-muted"
                        {...getNodeTestIdProp(
                            'revenue-dynamics-legend-caption',
                        )}
                    >
                        {copy.legendCaption}
                    </div>
                    <RevenueDynamicsKpis data={data} />
                </Widget.Content>
            </Widget>
        </section>
    );
};
